import React, { useEffect, useState } from "react";
import { generateCISummary } from "../../services/geminiService";
import LoadingSpinner from "../shared/LoadingSpinner";
import { TONES, ICONS } from "../shared/tones";

/**
 * Quadrant verdict from RPI × OPI.
 * - RPI band: <90 below, 90–110 at market, >110 premium
 * - OPI band: <90 slower, 90–110 in line, >110 faster
 */
export function computeVerdict({ rpi, opi, actionsCount = 0 } = {}) {
  if (rpi == null || opi == null) {
    return {
      tone: "gray",
      icon: "compass",
      label: "Insufficient data",
      headline: "Waiting on enough comp data to score your position.",
      subline: "A verdict appears once you have at least one comp-set sync with rate and occupancy.",
      cta: null,
    };
  }

  const rpiAbove = rpi > 110;
  const rpiAtMarket = rpi >= 90 && rpi <= 110;
  const rpiBelow = rpi < 90;
  const opiFaster = opi > 110;
  const opiInLine = opi >= 90 && opi <= 110;
  const opiSlower = opi < 90;

  const pricedPctAbs = Math.abs(rpi - 100).toFixed(0);
  const fillPctAbs = Math.abs(opi - 100).toFixed(0);

  const actionsCta = actionsCount > 0
    ? { label: `See ${actionsCount} AI action${actionsCount === 1 ? "" : "s"}`, href: "#ai-pricing-actions" }
    : null;

  // High RPI + High OPI → Winning
  if (rpiAbove && opiFaster) {
    return {
      tone: "emerald",
      icon: "trophy",
      label: "Winning",
      headline: `Priced ${pricedPctAbs}% above market and filling ${fillPctAbs}% faster.`,
      subline: "Premium positioning is being absorbed — room to test further rate lifts on peak dates.",
      cta: actionsCta,
    };
  }

  // High RPI + Low OPI → Watch (the current Verde Ranch state)
  if (rpiAbove && opiSlower) {
    return {
      tone: "amber",
      icon: "alert",
      label: "Watch",
      headline: `Holding a ${pricedPctAbs}% price premium; bookings pacing ${fillPctAbs}% behind market.`,
      subline: "You're priced above the market but filling slower. Review weekday rates, or give bookings more time to catch up.",
      cta: actionsCta,
    };
  }

  // Low RPI + High OPI → Act now (leaving money on the table)
  if (rpiBelow && opiFaster) {
    return {
      tone: "red",
      icon: "bolt",
      label: "Act now",
      headline: `Priced ${pricedPctAbs}% below market and filling ${fillPctAbs}% faster.`,
      subline: "Demand is outpacing your pricing — raise rates on the highest-velocity dates.",
      cta: actionsCta,
    };
  }

  // Low RPI + Low OPI → Reposition
  if (rpiBelow && opiSlower) {
    return {
      tone: "gray",
      icon: "compass",
      label: "Reposition",
      headline: `Priced ${pricedPctAbs}% below market and filling ${fillPctAbs}% slower.`,
      subline: "Pricing alone won't fix this — review listings, photos, and channel mix alongside rates.",
      cta: actionsCta,
    };
  }

  // At-market RPI bands
  if (rpiAtMarket && opiFaster) {
    return {
      tone: "emerald",
      icon: "trophy",
      label: "Raise tested",
      headline: `At market on rate, filling ${fillPctAbs}% faster than comp set.`,
      subline: "You have room to test rate increases on the dates where demand is strongest.",
      cta: actionsCta,
    };
  }
  if (rpiAtMarket && opiSlower) {
    return {
      tone: "amber",
      icon: "alert",
      label: "Watch",
      headline: `At market on rate, but filling ${fillPctAbs}% slower than comp set.`,
      subline: "Rates are competitive — investigate visibility, reviews, or restrictions.",
      cta: actionsCta,
    };
  }
  if (rpiAbove && opiInLine) {
    return {
      tone: "emerald",
      icon: "trophy",
      label: "Holding premium",
      headline: `Priced ${pricedPctAbs}% above market and filling in line with comp set.`,
      subline: "Your premium is sticking. Hold on weekdays, test lifts on holiday dates.",
      cta: actionsCta,
    };
  }
  if (rpiBelow && opiInLine) {
    return {
      tone: "amber",
      icon: "bolt",
      label: "Raise opportunity",
      headline: `Priced ${pricedPctAbs}% below market with comparable fill pace.`,
      subline: "You can likely lift rates without hurting pace — start with peak dates.",
      cta: actionsCta,
    };
  }

  // Default: at market on both
  return {
    tone: "gray",
    icon: "compass",
    label: "Balanced",
    headline: "Priced and pacing in line with the comp set.",
    subline: "No urgent moves. Keep an eye on rate-change feed and holiday premium capture.",
    cta: actionsCta,
  };
}

export default function VerdictCard({ summary, actionsCount = 0, clientId, pageVerdict = null }) {
  const [dismissed, setDismissed] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const metrics = summary?.data?.metrics;
  const verdict = computeVerdict({
    rpi: metrics?.rpi,
    opi: metrics?.opi,
    actionsCount,
  });
  const tone = TONES[verdict.tone] ?? TONES.gray;
  const dotColor = tone.dot;

  // AI-first narrative; deterministic copy (headline + subline) is the fallback.
  // No date range — CI is forward-looking (next 90 days), see geminiService.
  useEffect(() => {
    if (!clientId || !metrics || metrics.rpi == null || metrics.opi == null) {
      setAiText(null);
      return;
    }
    let cancelled = false;
    setAiLoading(true);
    const fallbackText = `${verdict.headline} ${verdict.subline}`;
    generateCISummary({ clientId, summary, actionsCount, fallbackText })
      .then((res) => {
        if (!cancelled) {
          setAiText(res.success ? res.data : null);
          setAiLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAiText(null);
          setAiLoading(false);
        }
      });
    return () => { cancelled = true; };
    // Depend on the primitive metrics the call actually keys off, not the
    // `summary` object — its reference changes on every parent render and
    // would re-fire the (paid) Gemini call. summary is still read inside.
  }, [clientId, metrics?.rpi, metrics?.opi, actionsCount]); // eslint-disable-line

  if (dismissed) return null;

  return (
    <div
      role="region"
      aria-label="Top insight"
      className={`relative ${tone.bg} border ${tone.border} rounded-xl p-4`}
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <svg className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {ICONS[verdict.icon] ?? ICONS.compass}
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Top Insight — {verdict.label}
          </h3>
          {aiLoading && !aiText ? (
            <LoadingSpinner label="Analyzing position..." inline />
          ) : aiText ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{aiText}</p>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <span className="font-medium text-gray-900 dark:text-gray-100">{verdict.headline}</span>{" "}
              {verdict.subline}
            </p>
          )}
          {/* B1 — page_verdict sentence (from ci_get_actions). Rendered as a
              regular body paragraph inside the Top Insight section, directly
              below the insight paragraph. Only renders when non-null — no
              placeholder, no loading state. Same text size/color as the insight
              paragraph above it. */}
          {pageVerdict && (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
              {pageVerdict}
            </p>
          )}
          {verdict.cta && (
            <div className={`flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t ${tone.divider}`}>
              <a
                href={verdict.cta.href}
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0`} />
                {verdict.cta.label} →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
